import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Maximize2, FileText, Download, Copy, ChevronRight, ChevronLeft } from 'lucide-react';
import { TableData } from '../utils/types';

import TableModal from './TableModal';
const Fragment = React.Fragment;

interface TableViewerProps {
    data: TableData;
}

const TableViewer: React.FC<TableViewerProps> = ({ data }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isColumnsExpanded, setIsColumnsExpanded] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const parseCSVData = (csvData: string) => {
        const rows = csvData.trim().split('\n');
        const headers = rows[0].split(',');

        const parsedData = rows.slice(1).map(row => {
            const values = row.split(',');
            const rowData: Record<string, string> = {};

            headers.forEach((header, index) => {
                rowData[header] = values[index] || '';
            });

            return rowData;
        });

        return { headers, rows: parsedData };
    };

    const parseData = () => {
        if (data.type === 'csv') {
            return parseCSVData(data.data);
        } else if (data.type === 'json') {
            try {
                // Ensure we have a JS object to work with
                const jsonData = typeof data.data === 'string'
                    ? JSON.parse(data.data)
                    : data.data;

                console.log("TableViewer processing JSON data:", jsonData);

                // If it's an array of objects (our most common case)
                if (Array.isArray(jsonData)) {
                    if (jsonData.length === 0) {
                        return { headers: [], rows: [] };
                    }

                    // Get all possible headers from all objects
                    const headers = Array.from(
                        new Set(
                            jsonData.flatMap(obj => Object.keys(obj))
                        )
                    );

                    // Create rows with all headers
                    const rows = jsonData.map(obj => {
                        const row: Record<string, string> = {};
                        headers.forEach(header => {
                            const value = obj[header];
                            row[header] = value !== undefined && value !== null
                                ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                                : '';
                        });
                        return row;
                    });

                    return { headers, rows };
                }

                // If it's a single object, convert to a single row table
                if (typeof jsonData === 'object' && jsonData !== null) {
                    const headers = Object.keys(jsonData);
                    const rows = [
                        headers.reduce((row, header) => {
                            const value = jsonData[header];
                            row[header] = value !== undefined && value !== null
                                ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                                : '';
                            return row;
                        }, {} as Record<string, string>)
                    ];
                    return { headers, rows };
                }
            } catch (error) {
                console.error('Error parsing JSON data:', error);
                // If there's an error, try to display the raw data
                return {
                    headers: ['Raw Data'],
                    rows: [{ 'Raw Data': typeof data.data === 'string' ? data.data : JSON.stringify(data.data) }]
                };
            }
        }

        return { headers: [], rows: [] };
    };

    const { headers, rows } = parseData();

    const copyToClipboard = () => {
        let textToCopy;
        
        if (data.type === 'json') {
            if (typeof data.data === 'string') {
                try {
                    // Prettify JSON if it's a string
                    textToCopy = JSON.stringify(JSON.parse(data.data), null, 2);
                } catch (e) {
                    console.error('Error formatting JSON for clipboard:', e);
                    textToCopy = data.data;
                }
            } else {
                // It's already a JavaScript object, stringify it
                textToCopy = JSON.stringify(data.data, null, 2);
            }
        } else {
            // CSV or other formats
            textToCopy = data.data;
        }

        navigator.clipboard.writeText(textToCopy)
            .then(() => {
                console.log('Successfully copied to clipboard');
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
                alert('Failed to copy to clipboard');
            });
    };

    const downloadData = () => {
        let content = data.data;
        let mimeType = 'text/csv';
        let fileExtension = 'csv';

        if (data.type === 'json') {
            if (typeof data.data === 'string') {
                try {
                    // Pretty print JSON if it's a string
                    content = JSON.stringify(JSON.parse(data.data), null, 2);
                } catch (e) {
                    console.error('Error formatting JSON for download:', e);
                }
            } else {
                content = JSON.stringify(data.data, null, 2);
            }
            mimeType = 'application/json';
            fileExtension = 'json';
        }

        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data.title || 'data'}.${fileExtension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const toggleColumnsExpansion = () => {
        setIsColumnsExpanded(!isColumnsExpanded);
    };

    const displayRows = isExpanded ? rows : rows.slice(0, 5);
    const hasMoreRows = rows.length > 5;

    const isWideTable = headers.length > 6;

    const displayColumns = isColumnsExpanded || !isWideTable ? headers : headers.slice(0, 5);

    return (
        <>
            <div className="mt-4 mb-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <div className="flex items-center">
                        <FileText size={16} className="mr-2 text-blue-600" />
                        <h3 className="font-medium text-gray-700">{data.title || 'Table Data'}</h3>
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={copyToClipboard}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Copy to clipboard"
                        >
                            {copySuccess ? (
                                <span className="text-xs text-green-600">Copied!</span>
                            ) : (
                                <Copy size={16} />
                            )}
                        </button>
                        <button
                            onClick={downloadData}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title={`Download ${data.type.toUpperCase()}`}
                        >
                            <Download size={16} />
                        </button>
                        <button
                            onClick={openModal}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Open fullscreen"
                        >
                            <Maximize2 size={16} />
                        </button>
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title={isExpanded ? "Show less" : "Show more"}
                        >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto" style={{ maxWidth: '100%', position: 'relative' }}>
                    {isWideTable && !isColumnsExpanded && (
                        <div
                            className="text-xs text-blue-600 bg-blue-50 p-1 m-2 rounded inline-flex items-center cursor-pointer hover:bg-blue-100 transition-colors"
                            onClick={toggleColumnsExpansion}
                        >
                            <ChevronRight size={12} className="mr-1" />
                            <span>Table is wide. Click to expand and see all columns.</span>
                        </div>
                    )}

                    <table className="w-full text-sm text-left table-auto border-collapse">
                        <thead className="bg-gray-50 text-gray-700">
                        <tr>
                            {displayColumns.map((header, index) => (
                                <th key={index} className="px-4 py-2 font-medium border-b border-gray-200 whitespace-nowrap">
                                    {header}
                                </th>
                            ))}

                            {isWideTable && !isColumnsExpanded && (
                                <th
                                    className="px-4 py-2 font-medium border-b border-gray-200 text-blue-600 cursor-pointer hover:bg-blue-50"
                                    onClick={toggleColumnsExpansion}
                                >
                                    <div className="text-xs">
                                        +{headers.length - 5} more columns
                                    </div>
                                </th>
                            )}

                            {isWideTable && isColumnsExpanded && (
                                <th
                                    className="px-4 py-2 font-medium border-b border-gray-200 text-blue-600 cursor-pointer hover:bg-blue-50 sticky right-0 bg-white shadow-md"
                                    onClick={toggleColumnsExpansion}
                                >
                                    <div className="flex items-center justify-center bg-blue-100 text-blue-600 p-1 rounded">
                                        <ChevronLeft size={16} />
                                    </div>
                                </th>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {displayRows.map((row, rowIndex) => (
                            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-blue-50'}>
                                {displayColumns.map((header, colIndex) => (
                                    <td key={colIndex} className="px-4 py-2 border-b border-gray-200 whitespace-nowrap">
                                        {row[header]}
                                    </td>
                                ))}

                                {isWideTable && !isColumnsExpanded && (
                                    <td className="px-4 py-2 border-b border-gray-200 text-blue-600 whitespace-nowrap text-center">
                                        <button
                                            onClick={toggleColumnsExpansion}
                                            className="text-blue-600 hover:text-blue-800"
                                            title="Show all columns"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </td>
                                )}

                                {isWideTable && isColumnsExpanded && (
                                    <td className="px-4 py-2 border-b border-gray-200 text-blue-600 whitespace-nowrap text-center sticky right-0 bg-white shadow-md">
                                        <button
                                            onClick={toggleColumnsExpansion}
                                            className="text-blue-600 hover:text-blue-800"
                                            title="Hide extra columns"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                {hasMoreRows && (
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-center">
                        {!isExpanded ? (
                            <button
                                onClick={() => setIsExpanded(true)}
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center w-full"
                            >
                                <span>Show all {rows.length - 5} more rows</span>
                                <ChevronDown size={14} className="ml-1" />
                            </button>
                        ) : rows.length > 10 && (
                            <button
                                onClick={() => setIsExpanded(false)}
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center w-full"
                            >
                                <span>Show less rows</span>
                                <ChevronUp size={14} className="ml-1" />
                            </button>
                        )}
                    </div>
                )}

                <div className="md:hidden px-4 py-2 bg-gray-50 border-t border-gray-200 text-center">
                    <button
                        onClick={openModal}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center w-full"
                    >
                        <Maximize2 size={14} className="mr-1" />
                        <span>View fullscreen</span>
                    </button>
                </div>
            </div>

            <TableModal
                data={data}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </>
    );
};

export default TableViewer;